/**
 * Short, human label for the browser issuing a pairing ("Chrome · macOS").
 * Sent as `issuerLabel` so the phone can show where the code came from.
 * Best effort and deterministic for a given user-agent input.
 */

interface UserAgentBrand {
  brand: string
  version?: string
}

/** The subset of `navigator.userAgentData` (UA Client Hints) we read. */
export interface UserAgentDataLike {
  brands?: readonly UserAgentBrand[]
  platform?: string
  mobile?: boolean
}

export interface IssuerSource {
  userAgent?: string
  userAgentData?: UserAgentDataLike | null
}

const BRAND_NAMES: ReadonlyArray<[RegExp, string]> = [
  [/^microsoft edge$/i, 'Edge'],
  [/^opera$/i, 'Opera'],
  [/^brave$/i, 'Brave'],
  [/^vivaldi$/i, 'Vivaldi'],
  [/^google chrome$/i, 'Chrome'],
  [/^chromium$/i, 'Chromium']
]

const UA_BROWSERS: ReadonlyArray<[RegExp, string]> = [
  [/\bEdg(e|A|iOS)?\//, 'Edge'],
  [/\b(OPR|Opera)\//, 'Opera'],
  [/\bSamsungBrowser\//, 'Samsung Internet'],
  [/\b(Firefox|FxiOS)\//, 'Firefox'],
  [/\b(Chrome|CriOS)\//, 'Chrome'],
  [/\bChromium\//, 'Chromium'],
  [/\bVersion\/[\d.]+.*Safari\//, 'Safari']
]

const UA_PLATFORMS: ReadonlyArray<[RegExp, string]> = [
  [/\b(iPhone|iPad|iPod)\b/, 'iOS'],
  [/\bAndroid\b/, 'Android'],
  [/\bCrOS\b/, 'ChromeOS'],
  [/\bWindows\b/, 'Windows'],
  [/\bMac OS X\b|\bMacintosh\b/, 'macOS'],
  [/\bLinux\b/, 'Linux']
]

const PLATFORM_NAMES: Readonly<Record<string, string>> = {
  macos: 'macOS',
  windows: 'Windows',
  linux: 'Linux',
  android: 'Android',
  'chrome os': 'ChromeOS',
  chromeos: 'ChromeOS',
  ios: 'iOS'
}

function browserFromBrands(brands: readonly UserAgentBrand[] | undefined): string | null {
  if (!brands) return null
  for (const [pattern, name] of BRAND_NAMES) {
    if (brands.some((entry) => pattern.test(entry.brand.trim()))) return name
  }
  return null
}

function firstMatch(text: string, table: ReadonlyArray<[RegExp, string]>): string | null {
  for (const [pattern, name] of table) {
    if (pattern.test(text)) return name
  }
  return null
}

export function describeIssuer(source?: IssuerSource): string {
  const nav =
    source ??
    (typeof navigator === 'undefined'
      ? {}
      : {
          userAgent: navigator.userAgent,
          userAgentData: (navigator as Navigator & { userAgentData?: UserAgentDataLike })
            .userAgentData
        })
  const ua = nav.userAgent ?? ''
  const data = nav.userAgentData ?? null

  const browser = browserFromBrands(data?.brands) ?? firstMatch(ua, UA_BROWSERS)
  const hintPlatform = data?.platform?.trim()
  const platform =
    (hintPlatform ? (PLATFORM_NAMES[hintPlatform.toLowerCase()] ?? hintPlatform) : null) ??
    firstMatch(ua, UA_PLATFORMS)

  const label = [browser, platform].filter(Boolean).join(' · ')
  return label || 'Web browser'
}
